/**
 * GLSL shaders for volumetric clouds.
 *
 * Technique inspired by IQ's cloud rendering approach:
 * - Hash-based 3D value noise (no textures, no directional bias)
 * - Height gradient: thick at bottom, wispy at top
 * - Directional derivative lighting (1 extra sample, no light march)
 * - Front-to-back premultiplied alpha compositing
 * - Distance-based LOD (fewer octaves far away)
 * - Adaptive step sizing
 *
 * Adapted for spherical planetary cloud shells.
 */

export const cloudVertexShader = /* glsl */ `
  varying vec3 vWorldPosition;
  void main() {
    vec4 worldPos = modelMatrix * vec4(position, 1.0);
    vWorldPosition = worldPos.xyz;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`;

export const cloudFragmentShader = /* glsl */ `
  precision highp float;

  #define MAX_STEPS 120

  uniform float uTime;
  uniform vec3  uSunDirection;
  uniform vec3  uSunColor;
  uniform float uCloudInnerRadius;
  uniform float uCloudOuterRadius;
  uniform float uCoverage;          // base coverage (0-1), controls how much sky has clouds
  uniform float uDensityScale;      // FBM amplitude, controls cloud thickness
  uniform float uCoverageThreshold; // smoothstep cutoff for cloud edges (0.5-0.7)
  uniform float uNoiseFreq;         // noise frequency in world space
  uniform vec3  uWindOffset;        // animated wind displacement
  uniform int   uStepCount;
  uniform vec3  uAtmosphereColor;   // sky atmosphere tint (CPU-computed, varies with sun angle)
  uniform float uDayFactor;         // 0 = night, 1 = day

  varying vec3 vWorldPosition;

  // --- Hash-based 3D value noise (no texture, no directional bias) ---
  float hash3to1(vec3 p) {
    p = fract(p * vec3(0.1031, 0.1030, 0.0973));
    p += dot(p, p.yxz + 33.33);
    return fract((p.x + p.y) * p.z);
  }

  float noise(vec3 x) {
    vec3 i = floor(x);
    vec3 f = fract(x);
    f = f * f * (3.0 - 2.0 * f);

    float a = hash3to1(i);
    float b = hash3to1(i + vec3(1,0,0));
    float c = hash3to1(i + vec3(0,1,0));
    float d = hash3to1(i + vec3(1,1,0));
    float e = hash3to1(i + vec3(0,0,1));
    float g = hash3to1(i + vec3(1,0,1));
    float h = hash3to1(i + vec3(0,1,1));
    float k = hash3to1(i + vec3(1,1,1));

    return mix(mix(mix(a,b,f.x), mix(c,d,f.x), f.y),
               mix(mix(e,g,f.x), mix(h,k,f.x), f.y),
               f.z) * 2.0 - 1.0;
  }

  // --- FBM with variable octave count ---
  float fbm(vec3 p, int octaves) {
    float f = 0.0;
    f += 0.50000 * noise(p); p *= 2.02;
    if (octaves < 2) return f;
    f += 0.25000 * noise(p); p *= 2.03;
    if (octaves < 3) return f;
    f += 0.12500 * noise(p); p *= 2.01;
    if (octaves < 4) return f;
    f += 0.06250 * noise(p); p *= 2.02;
    if (octaves < 5) return f;
    f += 0.03125 * noise(p);
    return f;
  }

  // --- Ray-sphere intersection ---
  vec2 raySphere(vec3 ro, vec3 rd, float radius) {
    float b = dot(ro, rd);
    float c = dot(ro, ro) - radius * radius;
    float d = b * b - c;
    if (d < 0.0) return vec2(1e5, -1e5);
    float sd = sqrt(d);
    return vec2(-b - sd, -b + sd);
  }

  // --- Cloud density at world position ---
  float cloudMap(vec3 pos, int oct) {
    float shellThick = uCloudOuterRadius - uCloudInnerRadius;
    float h = (length(pos) - uCloudInnerRadius) / shellThick;

    // Low-frequency regional coverage: large clear/cloudy zones
    vec3 regional = pos * uNoiseFreq * 0.12 + uWindOffset * 0.3;
    float region = noise(regional) * 0.5 + 0.5; // 0-1

    // Detail FBM at normal frequency
    vec3 q = pos * uNoiseFreq + uWindOffset;
    float f = fbm(q, oct);

    // Combine: regional modulates local coverage
    float localCoverage = uCoverage + region * 0.15;

    // --- Noise-modulated bottom edge: creates 3D tufts hanging down ---
    // Two scales of underside noise determine where cloud bottom is
    float tufts = noise(pos * uNoiseFreq * 2.5 + uWindOffset * 0.5) * 0.5
                + noise(pos * uNoiseFreq * 6.0 + uWindOffset * 0.3) * 0.3;
    // Bottom edge varies from h=0.05 (deep tufts) to h=0.45 (high gaps)
    float bottomEdge = 0.25 + tufts * 0.25;

    // Height profile with noise-modulated bottom
    // Bottom: sharp cutoff at variable height (creates tufts/gaps)
    // Top: gradual fade for wispy tops
    float hProfile = smoothstep(bottomEdge - 0.08, bottomEdge + 0.08, h)
                   * (1.0 - smoothstep(0.35, 1.0, h));

    float raw = localCoverage + hProfile * 0.55 - 0.1 + uDensityScale * f;

    // Threshold with soft edges for wispy cloud boundaries
    float edgeSoftness = 0.15;
    return smoothstep(uCoverageThreshold, uCoverageThreshold + edgeSoftness, raw);
  }

  // --- Dither ---
  float blueNoiseDither(vec2 fc) {
    return fract(52.9829189 * fract(0.06711056 * fc.x + 0.00583715 * fc.y));
  }

  void main() {
    vec3 ro = cameraPosition;
    vec3 rd = normalize(vWorldPosition - cameraPosition);
    vec3 sunDir = normalize(uSunDirection);

    // --- Cloud shell intersection ---
    vec2 outerHit = raySphere(ro, rd, uCloudOuterRadius);
    vec2 innerHit = raySphere(ro, rd, uCloudInnerRadius);

    float camDist = length(ro);
    float tStart, tEnd;

    if (camDist > uCloudOuterRadius) {
      if (outerHit.x > outerHit.y) { gl_FragColor = vec4(0.0); return; }
      tStart = outerHit.x;
      tEnd = (innerHit.x < innerHit.y && innerHit.x > 0.0) ? innerHit.x : outerHit.y;
    } else if (camDist < uCloudInnerRadius) {
      if (innerHit.x > innerHit.y) { gl_FragColor = vec4(0.0); return; }
      tStart = innerHit.y;
      tEnd = outerHit.y;
    } else {
      tStart = 0.0;
      tEnd = (innerHit.x > 0.0 && innerHit.x < innerHit.y) ? innerHit.x : outerHit.y;
    }
    tStart = max(tStart, 0.0);
    if (tEnd <= tStart) { gl_FragColor = vec4(0.0); return; }

    float shellThick = uCloudOuterRadius - uCloudInnerRadius;
    float pathLen = min(tEnd - tStart, shellThick * 8.0);

    // Light sampling offset (world-space distance for directional derivative)
    float lightOffset = 0.3 / uNoiseFreq;
    // Clamp so it doesn't exceed shell thickness
    lightOffset = min(lightOffset, shellThick * 0.5);

    float minStep = shellThick * 0.008;

    // Dither start position
    float dither = blueNoiseDither(gl_FragCoord.xy);
    float t = tStart + dither * minStep;

    // --- Ray march ---
    vec4 sum = vec4(0.0);

    for (int i = 0; i < MAX_STEPS; i++) {
      if (i >= uStepCount || sum.a > 0.99) break;

      vec3 pos = ro + rd * t;
      float distInShell = t - tStart;

      // Adaptive step size: grows with distance through shell
      float dt = max(minStep, 0.025 * distInShell);

      // Distance LOD: fewer octaves further into the march
      int oct = 5 - int(log2(1.0 + distInShell / shellThick));
      oct = clamp(oct, 2, 5);

      float den = cloudMap(pos, oct);

      if (den > 0.01) {
        // --- Directional derivative lighting ---
        float denSun = cloudMap(pos + lightOffset * sunDir, oct);
        float dif = clamp((den - denSun) / 0.6, 0.0, 1.0);

        // --- Per-sample local day/night ---
        // dot(sunDir, normalize(pos)) gives the local sun elevation at this cloud point
        // Clouds on the lit hemisphere stay bright; dark side goes dark
        float localSunH = dot(sunDir, normalize(pos));
        float localDay = smoothstep(-0.1, 0.25, localSunH);

        // --- Atmosphere-responsive cloud coloring ---
        // Base cloud color: mostly white, subtle atmosphere tint
        vec3 brightCloud = mix(vec3(1.0, 0.97, 0.92), uAtmosphereColor, 0.08);
        // Shadow tint: desaturated atmosphere-tinted gray
        vec3 shadowTint = mix(vec3(0.55, 0.58, 0.65), uAtmosphereColor, 0.25);
        // Dense cores darken slightly toward shadow tint
        vec3 baseColor = mix(brightCloud, shadowTint, den * 0.4);
        // Lighting: neutral ambient + colored sun contribution
        float ambient = 0.45;
        float sunLit = 0.55 * dif;
        vec3 lin = vec3(ambient) + uSunColor * sunLit;
        // Night darkening based on local position on the sphere
        lin *= mix(0.08, 1.0, localDay);

        vec4 col = vec4(baseColor * lin, den);

        // Distance fade
        float fogT = distInShell / (shellThick * 4.0);
        col.xyz *= exp(-0.5 * fogT * fogT);

        // Opacity scaled by step size
        col.w = min(col.w * 0.4 * dt / minStep, 1.0);

        // Front-to-back premultiplied alpha compositing
        col.rgb *= col.a;
        sum += col * (1.0 - sum.a);
      }

      t += dt;
      if (distInShell + dt > pathLen) break;
    }

    gl_FragColor = clamp(sum, 0.0, 1.0);
  }
`;
